import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import CreateStageModal from '../components/CreateStageModal';
import CreateMultipleStagesModal from '../components/CreateMultipleStagesModal';
import EditStageModal from '../components/EditStageModal';
import GenerateTourModal from '../components/GenerateTourModal';
import CreateMatchModal from '../components/CreateMatchModal';
import CustomModal from '../components/CustomModal';
import { useI18n } from '../utilities/i18n';
import { formatKickoff, normalizeAdminMatch } from '../utilities/matchesApi';
import '../styles/stagesManagement.css';
import '../styles/createMatchModal.css';
import Loader from "react-loader-spinner";

function StagesManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();

  // Восстанавливаем состояние из localStorage
  const savedState = JSON.parse(localStorage.getItem('stagesManagementState') || '{}');

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(savedState.championship || null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(savedState.league || null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(savedState.season || null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedStageId, setExpandedStageId] = useState(savedState.expandedStageId || null);
  const [stageMatches, setStageMatches] = useState({});
  
  // Модальные окна
  const [isCreateStageModalOpen, setIsCreateStageModalOpen] = useState(false);
  const [isCreateMultipleModalOpen, setIsCreateMultipleModalOpen] = useState(false);
  const [isEditStageModalOpen, setIsEditStageModalOpen] = useState(false);
  const [isGenerateTourModalOpen, setIsGenerateTourModalOpen] = useState(false);
  const [isCreateMatchModalOpen, setIsCreateMatchModalOpen] = useState(false);
  const [selectedStageForEdit, setSelectedStageForEdit] = useState(null);
  const [selectedStageForGenerate, setSelectedStageForGenerate] = useState(null);
  const [selectedPreviousStageForGenerate, setSelectedPreviousStageForGenerate] = useState(null);
  const [selectedStageForMatch, setSelectedStageForMatch] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ visible: false, stageId: null, text: '', canConfirm: false });

  // Сохраняем состояние в localStorage при изменении
  useEffect(() => {
    if (selectedChampionship || selectedLeague || selectedSeason || expandedStageId) {
      localStorage.setItem('stagesManagementState', JSON.stringify({
        championship: selectedChampionship,
        league: selectedLeague,
        season: selectedSeason,
        expandedStageId: expandedStageId
      }));
    }
  }, [selectedChampionship, selectedLeague, selectedSeason, expandedStageId]);

  useEffect(() => {
    loadChampionships();
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadStages();
    }
  }, [selectedLeague, selectedSeason]);

  // Загружаем матчи для раскрытой стадии после загрузки стадий
  useEffect(() => {
    if (expandedStageId && stages.length > 0 && !stageMatches[expandedStageId]) {
      loadStageMatches(expandedStageId);
    }
  }, [expandedStageId, stages]);

  const loadChampionships = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const leaguesData = Array.isArray(response.data) ? response.data : response.data.data;

      if (leaguesData && leaguesData.length > 0) {
        // Группируем по championat_name (у нас нет championat_id)
        const championsMap = {};
        
        leaguesData.forEach(item => {
          const championatName = item.championat_name || 'Без чемпионата';
          
          if (!championsMap[championatName]) {
            championsMap[championatName] = {
              value: championatName, // Используем название как value
              name: championatName,
              leagues: []
            };
          }
          
          if (item.league_id > 0) {
            championsMap[championatName].leagues.push({
              value: item.league_id,
              name: item.league_name,
              seasons: item.seasons || [],
              ...item
            });
          }
        });

        const formattedChampionships = Object.values(championsMap)
          .filter(c => c.leagues.length > 0)
          .map((item, index) => ({
            ...item,
            key: index
          }));

        setChampionships(formattedChampionships);
        
        // Восстанавливаем выбранный чемпионат если он был сохранен
        if (savedState.championship) {
          const restoredChampionship = formattedChampionships.find(
            c => c.value === savedState.championship.value
          );
          if (restoredChampionship) {
            setSelectedChampionship(restoredChampionship);
            setLeagues(restoredChampionship.leagues);
            
            // Восстанавливаем лигу
            if (savedState.league) {
              const restoredLeague = restoredChampionship.leagues.find(
                l => l.value === savedState.league.value
              );
              if (restoredLeague) {
                setSelectedLeague(restoredLeague);
                const formattedSeasons = restoredLeague.seasons.map((item, index) => ({
                  value: item.season_id,
                  name: item.season_name,
                  key: index,
                  ...item
                }));
                setSeasons(formattedSeasons);
                
                // Восстанавливаем сезон
                if (savedState.season) {
                  const restoredSeason = formattedSeasons.find(
                    s => s.value === savedState.season.value
                  );
                  if (restoredSeason) {
                    setSelectedSeason(restoredSeason);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading championships:', error);
    }
  };

  const handleChampionshipChange = (championship_id) => {
    const championship = championships.find(c => c.value === championship_id);
    setSelectedChampionship(championship);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setStages([]);
    setExpandedStageId(null);

    if (championship && championship.leagues && championship.leagues.length > 0) {
      setLeagues(championship.leagues);
    } else {
      setLeagues([]);
    }
  };

  const handleLeagueChange = (league_id) => {
    const league = leagues.find(l => l.value === league_id);
    setSelectedLeague(league);
    setSelectedSeason(null);
    setStages([]);
    setExpandedStageId(null);

    if (league && league.seasons && league.seasons.length > 0) {
      const formattedSeasons = league.seasons.map((item, index) => ({
        value: item.season_id,
        name: item.season_name,
        key: index,
        ...item
      }));
      setSeasons(formattedSeasons);
    } else {
      setSeasons([]);
    }
  };

  const handleSeasonChange = (season_id) => {
    const season = seasons.find(s => s.value === season_id);
    setSelectedSeason(season);
    setExpandedStageId(null);
  };

  const loadStages = async () => {
    if (!selectedLeague || !selectedSeason) return;

    setLoading(true);
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/getLeagueStages`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const stagesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setStages(stagesData);
    } catch (error) {
      console.error('Error loading stages:', error);
      setStages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStageMatches = async (stageId) => {
    if (stageMatches[stageId]) return; // Уже загружено

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // Используем тот же метод, что и в мобильном приложении
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        {
          season_id: selectedSeason.value,
          stage_id: stageId
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      const venueCtx = {
        ...(selectedLeague || {}),
        venue_timezone: selectedLeague?.venue_timezone || selectedLeague?.timezone
          || selectedChampionship?.venue_timezone || selectedChampionship?.timezone,
        city_name: selectedChampionship?.city || selectedChampionship?.city_ru
          || selectedChampionship?.name || selectedLeague?.city_name || '',
        city: selectedChampionship?.city || selectedChampionship?.city_ru || '',
        championship: selectedChampionship,
        league: selectedLeague,
      };
      // Фильтруем матчи по stage_id; UTC → стенное время площадки
      const filteredMatches = matchesData
        .filter(match => match.stage_id === stageId || match.tour_id === stageId)
        .map((match) => normalizeAdminMatch(match, venueCtx))
        .filter((match) => match.id);
      
      setStageMatches(prev => ({
        ...prev,
        [stageId]: filteredMatches
      }));
    } catch (error) {
      console.error('Error loading stage matches:', error);
      setStageMatches(prev => ({
        ...prev,
        [stageId]: []
      }));
    }
  };

  const toggleStage = async (stageId) => {
    if (expandedStageId === stageId) {
      setExpandedStageId(null);
    } else {
      setExpandedStageId(stageId);
      
      // Проверяем, есть ли матчи в самой стадии
      const stage = stages.find(s => s.id === stageId);
      
      if (stage && stage.matches && stage.matches.length > 0) {
        // Матчи уже есть в объекте стадии (UTC → стена)
        const venueCtx = {
          ...(selectedLeague || {}),
          venue_timezone: selectedLeague?.venue_timezone || selectedLeague?.timezone
            || selectedChampionship?.venue_timezone || selectedChampionship?.timezone,
          city_name: selectedChampionship?.city || selectedChampionship?.city_ru
            || selectedChampionship?.name || selectedLeague?.city_name || '',
          city: selectedChampionship?.city || selectedChampionship?.city_ru || '',
          championship: selectedChampionship,
          league: selectedLeague,
        };
        setStageMatches(prev => ({
          ...prev,
          [stageId]: stage.matches
            .map((match) => normalizeAdminMatch(match, venueCtx))
            .filter((match) => match.id),
        }));
      } else if (!stageMatches[stageId]) {
        // Загружаем матчи через API
        await loadStageMatches(stageId);
      }
    }
  };

  const handleEditStage = (stage) => {
    setSelectedStageForEdit(stage);
    setIsEditStageModalOpen(true);
  };

  const handleGenerateTour = (stage) => {
    setSelectedStageForGenerate(stage);
    
    // Находим предыдущий тур (стадию с наибольшим id, меньшим чем у текущей)
    const previousStage = stages
      .filter(s => s.id < stage.id)
      .sort((a, b) => b.id - a.id)[0] || null;
    
    setSelectedPreviousStageForGenerate(previousStage);
    setIsGenerateTourModalOpen(true);
  };

  const handleCreateMatch = (stage) => {
    setSelectedStageForMatch(stage);
    setIsCreateMatchModalOpen(true);
  };

  const handleMatchCreated = async () => {
    setIsCreateMatchModalOpen(false);
    
    // Перезагружаем матчи для текущей стадии
    if (selectedStageForMatch && selectedLeague && selectedSeason) {
      const stageId = selectedStageForMatch.id;
      
      try {
        const isTokenExpired = checkIfTokenExpired();
        if (isTokenExpired) {
          await dispatch(refreshTokens());
        }
        const tokens = getTokensFromStore();

        const response = await axios.post(
          BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
          {
            season_id: selectedSeason.value,
            stage_id: stageId
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        const filteredMatches = matchesData.filter(match => 
          match.stage_id === stageId || match.tour_id === stageId
        );
        
        setStageMatches(prev => ({
          ...prev,
          [stageId]: filteredMatches
        }));
        
        // Раскрываем стадию если она была закрыта
        setExpandedStageId(stageId);
      } catch (error) {
        console.error('Ошибка при загрузке матчей:', error);
      }
    }
  };

  const handleTourGenerated = async () => {
    setIsGenerateTourModalOpen(false);
    
    // Принудительно перезагружаем матчи для раскрытой стадии
    if (selectedStageForGenerate && selectedLeague && selectedSeason) {
      const stageId = selectedStageForGenerate.id;
      
      try {
        const isTokenExpired = checkIfTokenExpired();
        if (isTokenExpired) {
          await dispatch(refreshTokens());
        }
        const tokens = getTokensFromStore();

        const response = await axios.post(
          BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
          {
            season_id: selectedSeason.value,
            stage_id: stageId
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        
        // Фильтруем матчи по stage_id
        const filteredMatches = matchesData.filter(match => 
          match.stage_id === stageId || match.tour_id === stageId
        );
        
        setStageMatches(prev => ({
          ...prev,
          [stageId]: filteredMatches
        }));
        
        // Раскрываем стадию если она была закрыта
        setExpandedStageId(stageId);
      } catch (error) {
        console.error('Ошибка при загрузке матчей:', error);
        setStageMatches(prev => ({
          ...prev,
          [stageId]: []
        }));
      }
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const matchesResponse = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        {
          season_id: selectedSeason.value,
          stage_id: stageId
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const matchesData = Array.isArray(matchesResponse.data) ? matchesResponse.data : (matchesResponse.data.data || []);
      const stageMatchesCount = matchesData.filter(match =>
        match.stage_id === stageId || match.tour_id === stageId
      ).length;

      if (stageMatchesCount > 0) {
        setDeleteModal({
          visible: true,
          stageId: null,
          canConfirm: false,
          text: t('stDeleteHasMatches', { count: stageMatchesCount }),
        });
        return;
      }

      setDeleteModal({
        visible: true,
        stageId,
        canConfirm: true,
        text: t('stDeleteConfirm'),
      });
    } catch (error) {
      console.error('Error deleting stage:', error);
      setDeleteModal({
        visible: true,
        stageId: null,
        canConfirm: false,
        text: t('stDeleteError'),
      });
    }
  };

  const confirmDeleteStage = async () => {
    const stageId = deleteModal.stageId;
    if (!stageId) {
      setDeleteModal({ visible: false, stageId: null, text: '', canConfirm: false });
      return;
    }
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      await axios.post(
        BASE_URL.url + `/leagues/deleteStage/${stageId}`,
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      setDeleteModal({ visible: false, stageId: null, text: '', canConfirm: false });
      loadStages();
    } catch (error) {
      console.error('Error deleting stage:', error);
      setDeleteModal({
        visible: true,
        stageId: null,
        canConfirm: false,
        text: t('stDeleteError'),
      });
    }
  };

  const handleStageCreated = () => {
    // Перезагружаем список стадий
    loadStages();
    // Сбрасываем expandedStageId и stageMatches, чтобы пользователь мог заново раскрыть стадию
    setExpandedStageId(null);
    setStageMatches({});
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  return (
    <div className="layoutWrapper">
      <div className="matchCenterWrapper stages-page">
        <div className="matchCenterHeader">
          <span className="matchCenterName">{t('stTitle')}</span>
        </div>

        <div className="stages-content">
        <div className="stages-filters">
          <div className="filter-group">
            <label>{t('championship')}</label>
            <SelectSearch
              options={championships}
              search
              value={selectedChampionship?.value}
              onChange={handleChampionshipChange}
              filterOptions={fuzzySearch}
              emptyMessage={t('championshipNotFound')}
              placeholder={t('championshipPlaceholder')}
            />
          </div>

          {selectedChampionship && leagues.length > 0 && (
            <div className="filter-group">
              <label>{t('league')}</label>
              <SelectSearch
                options={leagues}
                search
                value={selectedLeague?.value}
                onChange={handleLeagueChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('leagueNotFound')}
                placeholder={t('leaguePlaceholder')}
              />
            </div>
          )}

          {selectedLeague && seasons.length > 0 && (
            <div className="filter-group">
              <label>{t('stSeason')}</label>
              <SelectSearch
                options={seasons}
                search
                value={selectedSeason?.value}
                onChange={handleSeasonChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('stSeasonNotFound')}
                placeholder={t('stSeasonPlaceholder')}
              />
            </div>
          )}

          {selectedLeague && selectedSeason && (
            <div className="stages-actions">
              <button
                className="btn-create-stage"
                onClick={() => setIsCreateStageModalOpen(true)}
              >
                <FiPlus /> {t('stCreate')}
              </button>
              <button
                className="btn-create-multiple"
                onClick={() => setIsCreateMultipleModalOpen(true)}
              >
                <FiPlus /> {t('stCreateMultiple')}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="stages-loading">
            <Loader type="TailSpin" color="#3ad1a0" height={40} width={40} />
            <span>{t('stLoading')}</span>
          </div>
        ) : selectedLeague && selectedSeason ? (
          stages.length > 0 ? (
            <div className="stages-list">
              {stages.map((stage) => (
                <div key={stage.id} className="stage-card">
                  <div className="stage-card-header" onClick={() => toggleStage(stage.id)}>
                    <div className="stage-info">
                      <h3>{stage.name}</h3>
                      {stage.short_name && (
                        <span className="stage-short-name">({stage.short_name})</span>
                      )}
                    </div>
                    <div className="stage-actions">
                      <button
                        className="btn-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStage(stage);
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      {(!stageMatches[stage.id] || stageMatches[stage.id].length === 0) && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStage(stage.id);
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                      <button className="btn-icon btn-expand">
                        {expandedStageId === stage.id ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                    </div>
                  </div>

                  {expandedStageId === stage.id && (
                    <div className="stage-card-body">
                      {/* Кнопки действий */}
                      <div className="generate-tour-section">
                        {/* Кнопка генерации матчей — для всех типов лиг */}
                        <button
                          className="btn-generate-tour"
                          onClick={() => handleGenerateTour(stage)}
                        >
                          <FiPlus /> {t('stGenerate')}
                        </button>
                        
                        <button
                          className="btn-create-match"
                          onClick={() => handleCreateMatch(stage)}
                        >
                          <FiPlus /> {t('stCreateMatch')}
                        </button>
                      </div>
                      
                      {stageMatches[stage.id] ? (
                        stageMatches[stage.id].length > 0 ? (
                          <div className="stage-matches">
                            {stageMatches[stage.id].map((match) => (
                              <Link
                                key={match.id}
                                to={`/manage_match/${match.id}`}
                                className="match-row"
                              >
                                <div className="match-date">
                                  {formatKickoff(match)}
                                </div>
                                <div className="match-teams">
                                  <span className="team-name">{match.home_team_name}</span>
                                  <span className="match-score">
                                    {match.status > 0 ? `${match.home_team_scores || 0}:${match.guest_team_scores || 0}` : 'vs'}
                                  </span>
                                  <span className="team-name">{match.guest_team_name}</span>
                                </div>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <div className="no-matches">
                            <p>{t('stNoMatches')}</p>
                          </div>
                        )
                      ) : (
                        <div className="stages-loading">
                          <Loader type="TailSpin" color="#3ad1a0" height={30} width={30} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-stages">
              <p>{t('stNoStages')}</p>
              <button
                className="btn-create-stage"
                onClick={() => setIsCreateStageModalOpen(true)}
              >
                <FiPlus /> {t('stCreateFirst')}
              </button>
            </div>
          )
        ) : (
          <div className="select-prompt">
            <p>{t('stSelectPrompt')}</p>
          </div>
        )}
        </div>

        <CreateStageModal
          isOpen={isCreateStageModalOpen}
          onClose={() => setIsCreateStageModalOpen(false)}
          onSuccess={handleStageCreated}
          league={selectedLeague}
          season={selectedSeason}
        />

        <CreateMultipleStagesModal
          isOpen={isCreateMultipleModalOpen}
          onClose={() => setIsCreateMultipleModalOpen(false)}
          onSuccess={handleStageCreated}
          league={selectedLeague}
          season={selectedSeason}
        />

        <EditStageModal
          isOpen={isEditStageModalOpen}
          onClose={() => setIsEditStageModalOpen(false)}
          onSuccess={handleStageCreated}
          stage={selectedStageForEdit}
          league={selectedLeague}
          season={selectedSeason}
        />

        <GenerateTourModal
          isOpen={isGenerateTourModalOpen}
          onClose={() => setIsGenerateTourModalOpen(false)}
          onSuccess={handleTourGenerated}
          stage={selectedStageForGenerate}
          previousStage={selectedPreviousStageForGenerate}
          league={selectedLeague}
          season={selectedSeason}
        />

        <CreateMatchModal
          isOpen={isCreateMatchModalOpen}
          onClose={() => setIsCreateMatchModalOpen(false)}
          onSuccess={handleMatchCreated}
          preselectedCountryId={selectedChampionship?.value || selectedChampionship?.id}
          preselectedCountryName={selectedChampionship?.name}
          preselectedLeague={selectedLeague}
          preselectedSeason={selectedSeason}
          preselectedStage={selectedStageForMatch}
        />

        <CustomModal
          styles={{ width: 440, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
          visible={deleteModal.visible}
          closeModal={() => setDeleteModal({ visible: false, stageId: null, text: '', canConfirm: false })}
        >
          <div className="st-delete-modal">
            <div className="customModalHeader">{t('stDeleteTitle')}</div>
            <p>{deleteModal.text}</p>
            <div className="st-delete-actions">
              <button
                type="button"
                className="btn-modal btn-secondary-modal"
                onClick={() => setDeleteModal({ visible: false, stageId: null, text: '', canConfirm: false })}
              >
                {t('cancel')}
              </button>
              {deleteModal.canConfirm && (
                <button type="button" className="btn-modal btn-danger-modal" onClick={confirmDeleteStage}>
                  {t('delete')}
                </button>
              )}
            </div>
          </div>
        </CustomModal>
      </div>
    </div>
  );
}

export default StagesManagement;
